/* eslint-disable no-nested-ternary */

import React, { useState, useEffect } from 'react';
import { map, drop, startCase, camelCase, forEach } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PageTitle, Button, ErrorBoundary } from '@cordata/buffet';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { all } from 'axios';

import useSlice from '~/utils/slice/hook';
import useReferenceData from '~/utils/reference-data/hook';
import useApi from '~/utils/api/hook';
import SlicePicker from '~/components/Slice/Picker';

import Collection from './Collection';
import Form from './Form';

const SliceCell = ({ data }) => {
  const [info, setInfo] = useState({});

  useEffect(() => {
    data
      .then(setInfo);
  }, []);

  return (
    <div className={`is-${info.type}`}>
      <span className="dot rounded-circle mr-2" />
      {info.type}{' '}
      {info.lineage && (<span className="text-muted">( <em>{info.lineage && ` ${info.lineage.join(', ')}`}</em> )</span>)}
    </div>
  );
};

SliceCell.propTypes = {
  data: PropTypes.shape().isRequired,
};

const ReferenceDataMegaCollection = () => {
  const {
    type,
    meta: { fields, typeAttr },
    config: {
      customFields = {},
      components = {},
      actions: {
        fetchReferenceData,
        afterRootSubmitSuccess = r => r,
      },
      initialValues: initialValuesFromConfig = {},
    },
  } = useReferenceData();
  const [id, setId] = useState(undefined);
  const [initialValues, setInitialValues] = useState({
    isActive: true,
    defaultSortOrder: 0,
    sortOrder: 0,
    ...initialValuesFromConfig,
  });
  const [status, setStatus] = useState('loading');
  const [tableState, setTableState] = useState({});
  const [rand, setRand] = useState();
  const {
    fetchAll,
    setCurrentSlice,
    serialize: serializeSlice,
    attachToItem,
  } = useSlice();
  const { config } = useApi();
  const [nameAttr] = useState(camelCase(drop(startCase(type).split(' ')).join(' ')));
  const {
    beforeCollection: BeforeCollection,
  } = components;

  useEffect(() => {
    fetchAll();
    setCurrentSlice('all');
  }, []);

  const handleItemClick = (e, handleOriginal, row) => (
    handleOriginal
      ? handleOriginal()
      : setId(row.original.id)
  );

  const handleSubmit = (values, bag) => {
    const method = values.id ? 'put' : 'post';
    const url = values.id ? `/${type}/${values.id}` : `/${type}`;

    forEach(fields, (v, k) => {
      if (
        v.required &&
        v.type === 'BOOLEAN' &&
        (values[k] === undefined || values[k] === null)
      ) {
        /* eslint-disable-next-line no-param-reassign */
        values[k] = false;
      }
    });

    forEach(fields, (v, k) => {
      if (
        v.type === 'UUID' &&
        v.references &&
        values[k] &&
        values[k].id
      ) {
        /* eslint-disable-next-line no-param-reassign */
        values[k] = values[k].id;
      }
    });

    if (typeAttr && values[typeAttr]) {
      /* eslint-disable-next-line no-param-reassign */
      values[typeAttr] = values[typeAttr].value;
    }

    const p = {
      ...values,
      ...serializeSlice(values),
      ...(
        !!customFields &&
        Object.keys(customFields).reduce((a, m) => ({
          ...a,
          [m]: customFields[m].serializeValue
            ? customFields[m].serializeValue(values[m])
            : values[m],
        }), {})
      ),
    };

    delete p.relationships;
    delete p.market;
    delete p.specialty;
    delete p.subSpecialty;
    delete p.org;
    delete p.division;

    config[method](url, p)
      .then(r =>
        afterRootSubmitSuccess({ r, values }))
      .then(() => {
        toast.success('Successfully saved!');

        setId(undefined);

        setRand(Math.random());

        fetchReferenceData(type);
      })
      .catch((e) => {
        toast.error('Oops! Something went wrong');
        console.error(e);
        bag.setErrors({ server: e });
      })
      .then(() => bag.setSubmitting(false));
  };

  useEffect(() => {
    setStatus('loading');

    if (id && id !== 'add') {
      config
        .get(`${type}/${id}`)
        .then(r => r.data)
        .then((r) => {
          const newR = r;
          const relationshipFetchPromises = [];

          map(fields, (v, k) => {
            if (v.type === 'UUID' && v.references && r.data.attributes[k]) {
              relationshipFetchPromises.push(config
                .get(`${v.references}/${r.data.attributes[k]}`)
                .then(rr => rr.data.data)
                .then((rr) => {
                  newR.data.attributes[k] = rr;

                  return rr;
                }));
            }
          });

          all(relationshipFetchPromises)
            .then(() => attachToItem(newR.data))
            .then((d) => {
              setInitialValues({
                id: d.id,
                relationships: d.relationships,
                isActive: id === 'add' ? true : d.attributes.isActive,
                ...d.attributes,
                ...(d.attributes &&
                  !!customFields &&
                  Object.keys(customFields).reduce((a, m) => ({
                    ...a,
                    [m]: d.attributes &&
                      customFields[m].normalizeValue
                      ? customFields[m].normalizeValue(d.attributes[m])
                      : d.attributes[m],
                  }), {})),
                ...(typeAttr && d.attributes && d.attributes[typeAttr] && {
                  [typeAttr]: ['label', 'value'].reduce((a, m) => ({
                    ...a,
                    [m]: d.attributes[typeAttr],
                  }), {}),
                }),
                dialect: 'us',
                language: 'en',
              });
            })
            .then(() => setStatus('success'));
        });
    } else if (id === 'add') {
      setInitialValues({
        ...(!!customFields && Object.keys(customFields).reduce((a, m) => ({
          ...a,
          [m]: customFields[m].initialValue,
        }), { isActive: true, defaultSortOrder: 0, sortOrder: 0 })),
        ...initialValuesFromConfig,
      });

      setStatus('success');
    }
  }, [id]);

  return (
    <ErrorBoundary>
      <PageTitle
        action={
          <Button
            outline
            onClick={() => setId('add')}
          >
            <FontAwesomeIcon icon="plus" />
            <span className="d-none d-md-inline ml-2">Add {startCase(nameAttr)}</span>
          </Button>
        }
      >
        {startCase(nameAttr)}
      </PageTitle>

      {BeforeCollection}

      <SlicePicker />

      <Collection
        type={type}
        onClick={handleItemClick}
        refreshData={rand}
        tableState={tableState}
        setTableState={setTableState}
      />

      {status === 'success' && !!initialValues && !!id && (
        <Form
          initialValues={initialValues}
          status={status}
          handleSubmit={handleSubmit}
          handleCancel={() => {
            setId();
            setInitialValues({ isActive: true });
          }}
        />
      )}
    </ErrorBoundary>
  );
};

export default ReferenceDataMegaCollection;
